import { BlogPost } from '@/types/blog'
import { Button } from '@/components/ui/button'
import {
  CheckCircle2,
  MessageCircle,
  Brain,
  Clock,
  Users,
  Home,
  Shield,
  Award,
  Smile,
  MapPin,
} from 'lucide-react'

const whatsappUrl =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20a%20import%C3%A2ncia%20do%20esporte%20aos%202%20anos%20e%20quero%20agendar%20uma%20aula%21'

export const postImportancia2Anos: BlogPost = {
  slug: 'por-que-2-anos-idade-mais-importante-comecar-esporte',
  title:
    'Por Que 2 Anos é a Idade Mais Importante Para Começar no Esporte (E Por Que Ninguém Fala Sobre Isso)',
  excerpt:
    'Descubra a "Janela de Ouro" do desenvolvimento infantil e como o método SuperFut aproveita o pico da neuroplasticidade para formar crianças mais confiantes e coordenadas.',
  date: '17 Fev, 2026',
  author: 'Nicholas Peters | SuperFut Kids',
  category: 'Desenvolvimento Infantil',
  location: 'São Paulo, SP',
  image:
    'https://img.usecurling.com/p/800/400?q=toddler%20playing%20soccer%20sunny',
  metaDescription:
    'Descubra por que 2 anos é a idade crucial para iniciar no esporte. Neurociência, desenvolvimento motor e o método SuperFut Kids explicado por Nicholas Peters.',
  faqTitle: '9. Perguntas Frequentes',
  faq: [
    {
      question: '2 anos não é cedo demais para futebol?',
      answer:
        'Não! É a "fase de ouro" da neuroplasticidade. O foco não é tático, mas sim desenvolvimento motor, sensorial e cognitivo através de brincadeiras lúdicas com a bola.',
    },
    {
      question: 'Preciso ter um espaço muito grande?',
      answer:
        'Não. Nossas aulas são adaptadas para a realidade de São Paulo: play do prédio, área comum, jardim ou até uma sala de estar espaçosa.',
    },
    {
      question: 'Em quanto tempo vejo resultados?',
      answer:
        'Varia de criança para criança, mas geralmente em 3 meses os pais notam grande melhora no equilíbrio, coordenação e, principalmente, na confiança.',
    },
    {
      question: 'Meu filho é muito tímido, como vocês lidam?',
      answer:
        'Nossa abordagem é 100% sem pressão. O professor cria vínculo primeiro, respeitando o espaço da criança, até que ela se sinta segura para participar.',
    },
    {
      question: 'E se ele não quiser participar no dia?',
      answer:
        'Respeitamos. Crianças de 2 anos têm dias e dias. O professor propõe atividades alternativas ou deixa a criança observar até querer voltar.',
    },
    {
      question: 'Preciso comprar equipamento?',
      answer:
        'Não se preocupe! Nós levamos todo o material necessário: bolas adequadas, cones coloridos, traves portáteis e materiais lúdicos.',
    },
    {
      question: 'Qual a qualificação dos professores?',
      answer:
        'Todos são treinados na metodologia SuperFut, focada na primeira infância, e passam por rigorosa checagem de antecedentes.',
    },
    {
      question: 'Posso fazer grupo com vizinhos?',
      answer:
        'Sim! Incentivamos muito os microgrupos (até 4 crianças). Ajuda na socialização e deixa o custo mais acessível para todos.',
    },
    {
      question: 'É seguro fazer em casa?',
      answer:
        'Sim. Nossos profissionais são verificados e treinados para identificar e mitigar riscos no ambiente doméstico antes da aula.',
    },
    {
      question: 'Como faço para agendar?',
      answer:
        'É simples! Clique no botão de WhatsApp nesta página e nossa equipe coordenará o melhor horário para ir até você.',
    },
  ],
  content: (
    <div className="space-y-10 text-slate-700 leading-relaxed">
      {/* 1. O Momento em Que Tudo Mudou */}
      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          1. O Momento em Que Tudo Mudou
        </h2>
        <p className="italic text-slate-600 border-l-4 border-super-yellow pl-4 mb-4">
          "Era uma tarde ensolarada em um parque de Los Angeles. Eu observava
          uma cena comum: uma mãe tentando ensinar seu filho, que aparentava ter
          uns 2 anos, a chutar uma bola."
        </p>
        <p>
          "Preste atenção!", ela dizia repetidamente. "Olhe para a bola!". Mas o
          menino estava fascinado por uma borboleta que passava, depois pela
          textura da grama, depois pelo barulho de um avião. A frustração da mãe
          era visível. Ela queria que ele jogasse futebol. Ele queria explorar o
          mundo.
        </p>
        <p>
          Naquele momento, percebi que o modelo tradicional de ensino de
          esportes estava quebrado para essa faixa etária. Não era culpa da
          criança, nem da mãe. Era um descompasso de expectativas e método. Foi
          ali que a semente do SuperFut nasceu.
        </p>
      </section>

      {/* 2. O Que Acontece no Cérebro */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Brain className="w-8 h-8 text-super-blue" />
          <h2 className="text-2xl font-bold text-super-text m-0">
            2. O Que Acontece no Cérebro aos 2 Anos
          </h2>
        </div>
        <p>
          Você sabia que aos 2 anos, o cérebro do seu filho está fazendo cerca
          de <strong>1 milhão de novas conexões neurais por segundo</strong>? É
          o pico da neuroplasticidade.
        </p>
        <p>
          A Academia Americana de Pediatria (AAP) e a OMS chamam isso de "Janela
          de Ouro". É o momento onde as bases da coordenação motora, equilíbrio
          e cognição estão sendo cimentadas. O que a criança vivencia agora
          define a arquitetura do cérebro dela para o resto da vida. O esporte
          nessa fase não é sobre formar o próximo Neymar, é sobre construir um
          cérebro potente e um corpo hábil.
        </p>
      </section>

      {/* 3. Por Que as Escolinhas Tradicionais Falham */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-8 h-8 text-red-500" />
          <h2 className="text-2xl font-bold text-super-text m-0">
            3. Por Que as Escolinhas Tradicionais Falham
          </h2>
        </div>
        <p>
          A maioria das escolinhas coloca 15, 20 crianças em uma quadra com um
          professor. Para uma criança de 2 anos, que tem um tempo de foco de 3 a
          5 minutos, isso é o caos.
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>Elas se perdem na multidão.</li>
          <li>Ficam muito tempo em filas (o que odeiam).</li>
          <li>O professor não consegue dar atenção individualizada.</li>
        </ul>
        <p className="mt-4">
          O resultado? A criança chora, não quer voltar e os pais acham que "ela
          não gosta de esporte". Errado. Ela só não gosta daquele formato.
        </p>
      </section>

      {/* 4. A Descoberta */}
      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          4. A Descoberta
        </h2>
        <p>
          Mergulhei nos estudos. De Piaget a Montessori, passando pela
          neurociência moderna. A resposta era clara: crianças de 2 anos
          aprendem brincando, em ambientes seguros e com atenção focada.
        </p>
        <p>
          Não adianta gritar "chuta!". Você precisa transformar o chute em uma
          missão: "Vamos derrubar o castelo do gigante (cones)!". A ludicidade é
          a linguagem universal dessa idade.
        </p>
      </section>

      {/* 5. A História do Miguel */}
      <section>
        <h2 className="text-2xl font-bold text-super-text mb-4">
          5. A História do Miguel
        </h2>
        <p>
          Miguel tinha 2 anos e 3 meses quando cheguei na casa dele. Ele se
          escondeu atrás da perna da mãe e chorou. Se fosse numa escolinha,
          provavelmente teriam dito "traz ele ano que vem".
        </p>
        <p>
          No SuperFut, sentei no chão. Comecei a girar a bola. Não chamei o
          Miguel. A curiosidade venceu. Em 10 minutos ele estava tocando na
          bola. Hoje, aos 4 anos, Miguel domina, corre e chuta com as duas
          pernas com uma confiança invejável. Ele não aprendeu só futebol; ele
          aprendeu que é capaz.
        </p>
      </section>

      {/* 6. Os 5 Pilares SuperFut */}
      <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
        <h2 className="text-2xl font-bold text-super-text mb-6">
          6. Os 5 Pilares do Método SuperFut
        </h2>
        <div className="grid gap-4">
          <div className="flex gap-3">
            <Home className="w-6 h-6 text-super-green flex-shrink-0" />
            <div>
              <h3 className="font-bold text-super-text">
                A Domicílio (No Seu Ambiente)
              </h3>
              <p className="text-sm">
                A criança aprende melhor onde se sente segura.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Users className="w-6 h-6 text-super-blue flex-shrink-0" />
            <div>
              <h3 className="font-bold text-super-text">
                Grupos Minúsculos (Max 4)
              </h3>
              <p className="text-sm">
                Atenção total do professor. Sem filas, sem espera.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Brain className="w-6 h-6 text-super-yellow flex-shrink-0" />
            <div>
              <h3 className="font-bold text-super-text">
                Metodologia Neuro-Compatível
              </h3>
              <p className="text-sm">
                Atividades desenhadas para o cérebro de 2 anos.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Award className="w-6 h-6 text-purple-500 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-super-text">
                Professores Especializados
              </h3>
              <p className="text-sm">
                Certificação baseada na US Soccer Federation e psicologia
                infantil.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Smile className="w-6 h-6 text-orange-500 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-super-text">
                Zero Pressão, 100% Diversão
              </h3>
              <p className="text-sm">
                O aprendizado é consequência da brincadeira, não uma obrigação.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Mas Meu Filho Não Presta Atenção */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-8 h-8 text-super-yellow" />
          <h2 className="text-2xl font-bold text-super-text m-0">
            7. "Mas Meu Filho Não Presta Atenção..."
          </h2>
        </div>
        <p>
          Calma. Isso é normal! Esperar foco de 30 minutos de uma criança de 2
          anos é biologicamente impossível.
        </p>
        <p>
          Nossa aula é dinâmica. Mudamos a atividade a cada 5 ou 7 minutos
          justamente para renovar o interesse. Se ele sair correndo para ver um
          pássaro, nós trazemos o foco de volta com outra brincadeira. Faz parte
          do processo.
        </p>
      </section>

      {/* 8. O Custo de Não Fazer Nada */}
      <section className="bg-red-50 p-8 rounded-3xl border border-red-100">
        <h2 className="text-2xl font-bold text-super-text mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-red-500" />
          8. O Custo de Não Fazer Nada
        </h2>
        <p className="mb-6 text-slate-700">
          A janela de desenvolvimento motor não fica aberta para sempre.
          Crianças que não são estimuladas nessa fase tendem a ter mais
          dificuldades motoras e sociais na idade escolar.
        </p>
        <p className="mb-6 font-medium text-slate-800">O SuperFut oferece:</p>
        <div className="grid grid-cols-2 gap-4 mb-8">
          {[
            'Coordenação Superior',
            'Autoconfiança',
            'Melhor Concentração',
            'Socialização Saudável',
            'Hábitos Ativos',
            'Vínculo Familiar',
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-2 text-sm text-slate-700"
            >
              <CheckCircle2 className="w-4 h-4 text-super-green" />
              {item}
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 py-6 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto animate-pulse"
            asChild
          >
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="w-6 h-6 mr-2" />
              Agendar Aula Experimental Gratuita
            </a>
          </Button>
          <p className="text-xs text-slate-500 mt-3">
            Vagas limitadas por região.
          </p>
        </div>
      </section>
    </div>
  ),
  contentAfterFaq: (
    <div className="space-y-12">
      {/* Footer/Bio Section */}
      <section className="bg-slate-900 text-slate-300 rounded-3xl overflow-hidden shadow-xl">
        <div className="p-8 md:p-10 flex flex-col md:flex-row gap-8 items-center md:items-start">
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-super-green flex-shrink-0 bg-slate-800 flex items-center justify-center">
            <span className="text-4xl font-bold text-slate-600">NP</span>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl font-display font-bold text-white mb-2">
              Nicholas Peters
            </h3>
            <p className="text-super-green font-medium mb-4">
              Fundador SuperFut Kids | Especialista em Desenvolvimento Motor
            </p>
            <p className="text-slate-400 mb-6 leading-relaxed text-sm">
              Com mais de 10 anos de experiência entre Los Angeles e Miami,
              Nicholas é certificado 3x pela{' '}
              <strong>US Soccer Federation</strong>. Dedicou sua carreira a
              entender como o esporte pode transformar a primeira infância,
              unindo a paixão brasileira pelo futebol com a metodologia
              americana de ensino estruturado.
            </p>

            <div className="border-t border-slate-700 pt-6">
              <h4 className="text-white font-bold text-sm mb-3 flex items-center justify-center md:justify-start gap-2">
                <MapPin className="w-4 h-4 text-super-blue" />
                Áreas de Atendimento Exclusivo:
              </h4>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {[
                  'Moema',
                  'Vila Olímpia',
                  'Itaim Bibi',
                  'Pinheiros',
                  'Alto de Pinheiros',
                ].map((area) => (
                  <span
                    key={area}
                    className="bg-slate-800 text-slate-300 px-3 py-1 rounded-full text-xs border border-slate-700 hover:border-super-blue transition-colors"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  ),
}
